import React, {useEffect, useRef, useState} from 'react'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import {
  DocumentReference,
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore'
import {useAuth} from '../../../../modules/auth'
import db from '../../../../../services/firebase'
import {ToastContainer} from 'react-toastify'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'

export interface addAsset {
  msg: string
  message: string
  checked?: boolean | undefined
}
interface AddAssetsProps {
  isOpen: boolean
  onClose: () => void
}

const ProductDrawer: React.FC<AddAssetsProps> = ({isOpen, onClose}) => {
  const [productName, setProductName] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [productUrl, setProductUrl] = useState('')
  const [contactUrl, setContactUrl] = useState('')
  const [loginUrl, setLoginUrl] = useState('')
  const [policyUrl, setPolicyUrl] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const {currentUser} = useAuth()
  const [isEditMode, setIsEditMode] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const focusRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
  
    if (currentUser?.tenantId) {
      const tenantRef = doc(db, 'tenants', currentUser.tenantId)
      const companyRef = doc(tenantRef, 'settings', 'product')
  
      unsubscribe = onSnapshot(companyRef, (docSnap) => {
        if (docSnap.exists()) {
          const productData = docSnap.data().newAsset || {}
  
          setProductName(productData.productName || '')
          setWebsiteUrl(productData.websiteUrl || '')
          setProductUrl(productData.productUrl || '')
          setContactUrl(productData.contactUrl || '')
          setLoginUrl(productData.loginUrl || '')
          setPolicyUrl(productData.policyUrl || '')
          setIsEditMode(true)
        } else {
          setIsEditMode(false)
        }
      })
    }
  
    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [currentUser])

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!productName.trim()) {
      errors.productName = 'Product name is required'
    }

    if (!websiteUrl.trim()) {
      errors.websiteUrl = 'Website name is required'
    }

    if (productUrl.length === 0) {
      errors.productUrl = 'Product URL is required'
    }

    if (!contactUrl.trim()) {
      errors.contactUrl = 'Contact URL is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    const newAsset = {
      productName,
      websiteUrl,
      productUrl,
      contactUrl,
      loginUrl,
      policyUrl,
      tenantId: currentUser?.tenantId,
    }

    if (!validateForm()) {
      setIsLoading(false)
      return
    }

    try {
      if (currentUser) {
        const tenantRef = doc(db, 'tenants', currentUser.tenantId!)
        const productRef: DocumentReference = doc(tenantRef, 'settings', 'product')

        await setDoc(productRef, {newAsset})

        const currentDate = new Date()
        const todaysDate = `${
          currentDate.getMonth() + 1
        }/${currentDate.getDate()}/${currentDate.getFullYear()}`

        const activitiesRef = collection(tenantRef, 'activities')
        await addDoc(activitiesRef, {
          message: isEditMode
            ? `Updated the product details for '${productName}'`
            : `Added a new product '${productName}' details.`,
          timestamp: serverTimestamp(),
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          username: currentUser?.userName,
          date: todaysDate,
          action: 'Product Added',
          target: 'Settings',
          email: currentUser?.email,
        })
        showSuccessToast({message: 'Product details updated successfully'})
        setIsEditMode(true)
      }
    } catch (error) {
      showErrorToast({message: 'Failed to update product details'})
    } finally {
      setIsLoading(false)
      onClose()
      window.scrollTo(0, 0)
    }
  }

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} direction='right' className='' size='45%'>
        <div
          className=' modal-dialog-centered border-bottom border-gray-300'
          style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            padding: '14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingLeft: '15px',
            paddingRight: '12px',
          }}
        >
          <div className='fs-4 fw-bold mx-2' style={{margin: 0}}>
            {productName ? 'Product Details' : 'Add Product'}
          </div>

          <div style={{display: 'flex', gap: '16px'}}>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary '
              onClick={onClose}
              aria-label='Close'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#5A5A5A'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-x'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M18 6l-12 12' />
                <path d='M6 6l12 12' />
              </svg>
            </div>
          </div>
        </div>
        <div
          className='scroll-y overflow-auto p-7 mb-4'
          tabIndex={-1}
          style={{height: 'calc(100vh - 30px)'}}
        >
          <form id='kt_risk_form' className='form' onSubmit={handleSubmit}>
            <div className='modal-content'>
              <div className='modal-body'>
                <div className=''>
                  <div className='form-group'>
                    <label className='text-dark fw-bold text-primary mb-2 fs-6 required'>
                      Product Name
                    </label>
                    <input
                      type='text'
                      className={`form-control ${formErrors.productName ? 'is-invalid' : ''}`}
                      placeholder='Enter product name'
                      name='productName'
                      value={productName}
                      onChange={(e) => {
                        setProductName(e.target.value)
                        setFormErrors((prev) => ({...prev, productName: ''}))
                      }}
                      ref={focusRef}
                    />
                    {formErrors.productName && (
                      <span className='text-danger'>{formErrors.productName}</span>
                    )}
                  </div>

                  <div className='form-group'>
                    <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6 required'>
                      Website/Landing Page URL
                    </label>

                    <input
                      type='url'
                      className={`form-control ${formErrors.websiteUrl ? 'is-invalid' : ''}`}
                      placeholder='Enter website name'
                      value={websiteUrl}
                      onChange={(e) => {
                        setWebsiteUrl(e.target.value)
                        setFormErrors((prev) => ({...prev, websiteUrl: ''}))
                      }}
                    />
                    {formErrors.websiteUrl && (
                      <span className='text-danger'>{formErrors.websiteUrl}</span>
                    )}
                  </div>

                  <div className='form-group'>
                    <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6 required'>
                      Product/Application URL
                    </label>
                    <input
                      type='url'
                      className={`form-control ${formErrors.productUrl ? 'is-invalid' : ''}`}
                      placeholder='Enter product/application URL'
                      value={productUrl}
                      onChange={(e) => {
                        setProductUrl(e.target.value)
                        setFormErrors((prev) => ({...prev, productUrl: ''}))
                      }}
                    />
                    {formErrors.productUrl && (
                      <span className='text-danger'>{formErrors.productUrl}</span>
                    )}
                  </div>

                  <div className='form-group'>
                    <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6 required'>
                      Contact Page URL
                    </label>

                    <input
                      type='url'
                      className={`form-control ${formErrors.contactUrl ? 'is-invalid' : ''}`}
                      placeholder='Enter contact page URL'
                      value={contactUrl}
                      onChange={(e) => {
                        setContactUrl(e.target.value)
                        setFormErrors((prev) => ({...prev, contactUrl: ''}))
                      }}
                    />
                    {formErrors.contactUrl && (
                      <span className='text-danger'>{formErrors.contactUrl}</span>
                    )}

                    <div className='fs-7' style={{color: '#828282'}}>
                      Your product’s contact us page URL
                    </div>
                  </div>

                  <div className='form-group'>
                    <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6'>
                      Login Page URL
                    </label>
                    <div className='input-group input-group-lg'>
                      <input
                        type='url'
                        className='form-control '
                        placeholder='Enter login page URL'
                        value={loginUrl}
                        onChange={(e) => setLoginUrl(e.target.value)}
                      />
                    </div>
                    <div className='fs-7' style={{color: '#828282'}}>
                      Your product’s login page URL
                    </div>
                  </div>

                  <div className='form-group mb-8'>
                    <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6'>
                      Privacy Policy Page URL
                    </label>
                    <div className='input-group input-group-lg'>
                      <input
                        type='url'
                        className='form-control '
                        placeholder='Enter privacy policy page URL'
                        value={policyUrl}
                        onChange={(e) => setPolicyUrl(e.target.value)}
                      />
                    </div>
                    <div className='fs-7' style={{color: '#828282'}}>
                      Your product’s privacy policy page URL
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div
          className='offcanvas-footer d-flex justify-content-end border-top py-4 px-6'
          style={{position: 'sticky', bottom: 0, backgroundColor: 'white'}}
        >
          <button type='button' className='btn btn-sm btn-light mx-2' onClick={onClose}>
            Cancel
          </button>
          {isLoading ? (
            <button
              type='submit'
              className='btn btn-sm'
              style={{backgroundColor: '#0E9F6E', color: '#FFFFFF'}}
            >
              <span className='spinner-border spinner-border-sm align-middle m-2'></span>
              {isEditMode ? 'Update Product' : 'Add Product'}
            </button>
          ) : (
            <button
              type='submit'
              className='btn btn-sm'
              style={{backgroundColor: '#0E9F6E', color: '#FFFFFF'}}
            >
              {isEditMode ? 'Update Product' : 'Add Product'}
            </button>
          )}
        </div>
      </Drawer>
      <ToastContainer />
    </>
  )
}

export default ProductDrawer
